import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from '../hooks/utils/useAuth';
import { setSessionStorageItem } from '../hooks/utils/useSessionStorage';
import { usePeersonaAccess } from '../hooks/utils/usePeersonaAccess';

/**
 * Phase A of the Fi + Peersona unified widget.
 *
 * The Fi bubble is the single floating entry point for the platform. Once the
 * widget is open, ``activeContext`` decides which surface is shown — Fi for
 * the concierge, or Peersona for a specific peer's lived-experience chat.
 *
 * Persistence/messaging stays inside FiProvider and PeersonaProvider. This
 * coordinator only owns the visibility/route question. FiContext.panelOpen
 * and PeersonaContext.panelOpen are derived from ``widgetOpen`` +
 * ``activeContext`` so the existing panel/header/bubble components keep
 * working without per-call rewrites.
 */

export type AssistantContext = { kind: 'fi' } | { kind: 'peersona'; chatbotId: number };

/**
 * Which view the widget is currently rendering. Shared across Fi and Peersona
 * panels so the clock / edit-icon switcher in the new header chrome lights up
 * the same view in whichever panel is active.
 *
 * - ``chat``: the normal conversation surface (default for every open)
 * - ``conversations``: list of Fi + each eligible Peersona's most recent thread
 * - ``new``: list of Fi + each eligible Peersona for starting a fresh chat
 *
 * The list views render in-place inside the current panel rather than as a
 * separate page, mirroring how ``PeersonaAboutDrawer`` already overlays the
 * conversation in ``PeersonaPanel``.
 */
export type AssistantPanelView = 'chat' | 'conversations' | 'new';

/** Persisted so a page reload re-opens the widget to Fi vs the last Peersona peer. */
const ACTIVE_CONTEXT_KEY = 'kh:assistant:activeContext';

const isAssistantContext = (value: unknown): value is AssistantContext => {
  if (!value || typeof value !== 'object') return false;
  const v = value as AssistantContext;
  if (v.kind === 'fi') return true;
  if (v.kind === 'peersona' && typeof v.chatbotId === 'number' && Number.isFinite(v.chatbotId)) return true;
  return false;
};

const readStoredActiveContext = (fallback: AssistantContext): AssistantContext => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_CONTEXT_KEY);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return isAssistantContext(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const writeStoredActiveContext = (ctx: AssistantContext): void => {
  setSessionStorageItem(ACTIVE_CONTEXT_KEY, JSON.stringify(ctx));
};

const clearStoredActiveContext = (): void => {
  try {
    sessionStorage.removeItem(ACTIVE_CONTEXT_KEY);
  } catch {
    /* storage disabled */
  }
};

interface AssistantWidgetContextValue {
  widgetOpen: boolean;
  activeContext: AssistantContext;
  isFiActive: boolean;
  isPeersonaActive: boolean;
  activePeersonaChatbotId: number | null;
  /** Which inner view is currently rendered inside whichever panel is active. */
  assistantPanelView: AssistantPanelView;
  /**
   * Open the widget. When ``target`` is provided the active context is
   * switched at the same time so callers can do open + switch atomically.
   * When omitted the widget opens to the current ``activeContext``.
   */
  openWidget: (target?: AssistantContext) => void;
  /** Hide the widget; preserves ``activeContext`` so re-opens land in the same place. */
  closeWidget: () => void;
  /** Toggle visibility. When closed, opens to ``target`` (or current activeContext). */
  toggleWidget: (target?: AssistantContext) => void;
  /**
   * Change the active context without affecting widget visibility. Also
   * resets the panel view back to ``chat`` so a switch from the Conversations
   * list (or any list view) lands the user on the conversation rather than
   * the same list inside the other panel.
   */
  switchContext: (target: AssistantContext) => void;
  /** Set the current panel view directly. Prefer the named helpers below. */
  setAssistantPanelView: (view: AssistantPanelView) => void;
  /** Show the chat view inside the current panel. */
  openChatView: () => void;
  /** Show the Conversations list inside the current panel. */
  openConversationsView: () => void;
  /** Show the Start-a-New-Conversation list inside the current panel. */
  openNewConversationView: () => void;
}

const AssistantWidgetContext = createContext<AssistantWidgetContextValue | null>(null);

export const useAssistantWidget = (): AssistantWidgetContextValue => {
  const ctx = useContext(AssistantWidgetContext);
  if (!ctx) throw new Error('useAssistantWidget must be used within AssistantWidgetProvider');
  return ctx;
};

interface ProviderProps {
  children: React.ReactNode;
  /** Default context to land on at first open. ``fi`` is the platform default. */
  initialContext?: AssistantContext;
}

export const AssistantWidgetProvider: React.FC<ProviderProps> = ({ children, initialContext = { kind: 'fi' } }) => {
  const { authToken } = useAuth();
  const { featureEnabled: peersonaEnabled } = usePeersonaAccess();
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [activeContext, setActiveContext] = useState<AssistantContext>(() => readStoredActiveContext(initialContext));
  const [assistantPanelView, setAssistantPanelViewState] = useState<AssistantPanelView>('chat');

  // Keep a ref in lockstep with state so togglers reading inside the same
  // tick (e.g. ``openWidget`` immediately after ``switchContext``) never see
  // a stale value.
  const activeContextRef = useRef<AssistantContext>(activeContext);
  activeContextRef.current = activeContext;

  useEffect(() => {
    writeStoredActiveContext(activeContext);
  }, [activeContext]);

  // Logout clears the auth token but leaves sessionStorage intact — drop the
  // stored assistant route so the next login doesn't inherit a prior user's
  // Peersona peer selection.
  useEffect(() => {
    if (authToken) return;
    clearStoredActiveContext();
    setActiveContext({ kind: 'fi' });
    activeContextRef.current = { kind: 'fi' };
  }, [authToken]);

  // Peersona off for this client: never resume a stale Peersona context
  // (KINDLY-1137 — Fi bubble would hide with no panel mounted).
  useEffect(() => {
    if (peersonaEnabled) return;
    if (activeContextRef.current.kind !== 'peersona') return;
    const fi: AssistantContext = { kind: 'fi' };
    setActiveContext(fi);
    activeContextRef.current = fi;
    writeStoredActiveContext(fi);
  }, [peersonaEnabled]);

  const openWidget = useCallback(
    (target?: AssistantContext) => {
      let next = target;
      if (next?.kind === 'peersona' && !peersonaEnabled) {
        next = { kind: 'fi' };
      }
      if (next) {
        setActiveContext(next);
        activeContextRef.current = next;
      } else if (!peersonaEnabled && activeContextRef.current.kind === 'peersona') {
        const fi: AssistantContext = { kind: 'fi' };
        setActiveContext(fi);
        activeContextRef.current = fi;
      }
      // Every panel open lands in chat view — list views are short-lived
      // navigations, not a sticky surface to come back to.
      setAssistantPanelViewState('chat');
      setWidgetOpen(true);
    },
    [peersonaEnabled],
  );

  const closeWidget = useCallback(() => {
    setWidgetOpen(false);
    // Reset on close so the next open is consistent regardless of where the
    // user was last (Conversations / Start-New list views shouldn't persist
    // across opens — they're navigations, not a destination).
    setAssistantPanelViewState('chat');
  }, []);

  const toggleWidget = useCallback(
    (target?: AssistantContext) => {
      setWidgetOpen((open) => {
        if (open) {
          setAssistantPanelViewState('chat');
          return false;
        }
        let next = target;
        if (next?.kind === 'peersona' && !peersonaEnabled) {
          next = { kind: 'fi' };
        }
        if (next) {
          setActiveContext(next);
          activeContextRef.current = next;
        } else if (!peersonaEnabled && activeContextRef.current.kind === 'peersona') {
          const fi: AssistantContext = { kind: 'fi' };
          setActiveContext(fi);
          activeContextRef.current = fi;
        }
        setAssistantPanelViewState('chat');
        return true;
      });
    },
    [peersonaEnabled],
  );

  const switchContext = useCallback((target: AssistantContext) => {
    setActiveContext(target);
    activeContextRef.current = target;
    // Always land on chat — switching contexts shouldn't keep the list view visible.
    setAssistantPanelViewState('chat');
  }, []);

  const setAssistantPanelView = useCallback((view: AssistantPanelView) => {
    setAssistantPanelViewState(view);
  }, []);

  const openChatView = useCallback(() => setAssistantPanelViewState('chat'), []);
  const openConversationsView = useCallback(() => setAssistantPanelViewState('conversations'), []);
  const openNewConversationView = useCallback(() => setAssistantPanelViewState('new'), []);

  const value = useMemo<AssistantWidgetContextValue>(() => {
    const isFiActive = activeContext.kind === 'fi';
    const isPeersonaActive = activeContext.kind === 'peersona';
    const activePeersonaChatbotId = activeContext.kind === 'peersona' ? activeContext.chatbotId : null;
    return {
      widgetOpen,
      activeContext,
      isFiActive,
      isPeersonaActive,
      activePeersonaChatbotId,
      assistantPanelView,
      openWidget,
      closeWidget,
      toggleWidget,
      switchContext,
      setAssistantPanelView,
      openChatView,
      openConversationsView,
      openNewConversationView,
    };
  }, [
    widgetOpen,
    activeContext,
    assistantPanelView,
    openWidget,
    closeWidget,
    toggleWidget,
    switchContext,
    setAssistantPanelView,
    openChatView,
    openConversationsView,
    openNewConversationView,
  ]);

  return <AssistantWidgetContext.Provider value={value}>{children}</AssistantWidgetContext.Provider>;
};
